<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import calendarize from 'calendarize';
  import InlineSVG from 'svelte-inline-svg';

  import previousIcon from '../../assets/icons/chevron-thin-left.svg';
  import nextIcon from '../../assets/icons/chevron-thin-right.svg';

  import DaySelection from './DaySelection.svelte';
  import MonthSelection from './MonthSelection.svelte';
  import YearSelection from './YearSelection.svelte';

  const dispatch = createEventDispatcher();

  export let type: 'date' | 'month' = 'date';
  export let selectedDate: Date | null = null;
  export let minDate: Date | null = null;
  export let maxDate: Date | null = null;

  const maxValue = maxDate ? maxDate.getTime() : Infinity;
  const minValue = minDate ? minDate.getTime() : 0;
  const initialCalendarDate = new Date(
    Math.min(maxValue, Math.max(minValue, Date.now()))
  );
  export let year = selectedDate
    ? selectedDate.getFullYear()
    : initialCalendarDate.getFullYear();
  export let month = selectedDate
    ? selectedDate.getMonth()
    : initialCalendarDate.getMonth();

  export let weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  export let weekdayOffset = 1; // Offset 1 to start the week on Monday

  let selectionType: 'day' | 'month' | 'year' =
    type === 'month' ? 'month' : 'day';

  let previousMonth = calendarize(new Date(year, month - 1), weekdayOffset);
  let currentMonth = calendarize(new Date(year, month), weekdayOffset);
  let nextMonth = calendarize(new Date(year, month + 1), weekdayOffset);

  $: currentMonthStart = new Date(year, month, 1, 0, 0, 0, 0);
  $: currentMonthEnd = new Date(year, month + 1, 1, 0, 0, 0, -1);
  $: currentYearStart = new Date(year, 0, 1, 0, 0, 0, 0);
  $: currentYearEnd = new Date(year, 12, 1, 0, 0, 0, -1);

  // Changes calendar month to the previous month
  const showPreviousMonth = () => {
    // Handle turn of the year
    if (--month < 0) {
      month = 11;
      year--;
    }

    nextMonth = currentMonth;
    currentMonth = previousMonth;
    previousMonth = calendarize(new Date(year, month - 1), weekdayOffset);
  };

  // Changes calendar month to the next month
  const showNextMonth = () => {
    // Handle turn of the year
    if (++month > 11) {
      month = 0;
      year++;
    }

    previousMonth = currentMonth;
    currentMonth = nextMonth;
    nextMonth = calendarize(new Date(year, month + 1), weekdayOffset);
  };

  // Changes calendar month to the given month
  const showMonth = (event: CustomEvent) => {
    month = event.detail;

    previousMonth = calendarize(new Date(year, month - 1), weekdayOffset);
    currentMonth = calendarize(new Date(year, month), weekdayOffset);
    nextMonth = calendarize(new Date(year, month + 1), weekdayOffset);

    if (type === 'month') {
      selectedDate = new Date(year, month, 1, 0, 0, 0, 0);
      dispatch('change', selectedDate);
    } else {
      // Timeout needed to not remove the clicked element before
      // event listeners can handle it (e.g. inside dropdowns)
      setTimeout(() => {
        selectionType = 'day';
      }, 0);
    }
  };

  // Changes calendar year to the previous year
  const showPreviousYear = () => {
    year--;
  };

  // Changes calendar year to the next year
  const showNextYear = () => {
    year++;
  };

  // Changes calendar year to the given year
  const showYear = (event: CustomEvent) => {
    year = event.detail;

    // Timeout needed to not remove the clicked element before
    // event listeners can handle it (e.g. inside dropdowns)
    setTimeout(() => {
      selectionType = 'month';
    }, 0);
  };
</script>

<style lang="postcss">
  .calendarWrapper {
    width: 100%;
    min-width: 15rem;
    max-width: 20rem;
    margin-bottom: 1.5rem;
    border: 1px solid var(--ubilabs-gray);
    border-bottom-left-radius: var(--ubilabs-calendar-border-radius);
    border-bottom-right-radius: var(--ubilabs-calendar-border-radius);
    background-color: var(--ubilabs-white);
  }

  .calendarHeader {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    height: var(--ubilabs-calendar-navigation-height);
  }

  .calendarHeader > button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: var(--ubilabs-calendar-navigation-height);
    padding: 0;
    background: none;
    color: var(--ubilabs-black);
    font-family: var(--ubilabs-font-family);
    font-size: var(--ubilabs-calendar-font-size);
    font-weight: 500;
  }

  .calendarHeader > button:not([disabled]):hover,
  .calendarHeader > button:not([disabled]):focus-visible {
    background-color: var(
      --ubilabs-calendar-navigation-button-hover-background-color
    );
  }

  .calendarHeader > .headerArrow {
    width: var(--ubilabs-calendar-navigation-height);
    color: var(--ubilabs-calendar-navigation-button-color);
    font-size: 0.625rem;
  }

  .calendarHeader > .headerArrow[disabled] {
    background-color: var(
      --ubilabs-calendar-navigation-button-disabled-background-color
    );
    color: var(--ubilabs-calendar-navigation-button-disabled-color);
  }

  .headerLabel {
    grid-column: 2;
  }

  .calendarContent {
    max-height: 14.5rem;
    padding: 1.25rem 0.5rem;
    overflow: auto;
  }

  .calendarContent :global(button) {
    height: var(--ubilabs-calendar-tile-size);
    margin: 0;
    padding: 0.125rem 0.375rem;
    border: none;
    border-radius: var(--ubilabs-calendar-tile-border-radius);
    outline: none;
    background: none;
    color: var(--ubilabs-black);
    font-family: var(--ubilabs-font-family);
    text-align: center;
    white-space: nowrap;
  }

  .calendarContent :global(button:not([disabled]):hover),
  .calendarContent :global(button:not([disabled]):focus-visible) {
    background-color: var(--ubilabs-calendar-tile-hover-background-color);
  }

  .calendarContent :global(button[disabled]) {
    background-color: var(--ubilabs-calendar-tile-disabled-background-color);
    color: var(--ubilabs-calendar-tile-disabled-color);
  }
</style>

<div class="calendarWrapper {$$props.class}">
  <header class="calendarHeader">
    {#if selectionType !== 'year'}
      <button
        class="headerArrow"
        disabled={minDate &&
          minDate >=
            (selectionType === 'month' ? currentYearStart : currentMonthStart)}
        on:click|preventDefault={selectionType === 'month'
          ? showPreviousYear
          : showPreviousMonth}>
        <InlineSVG src={previousIcon} />
      </button>
    {/if}

    <button
      class="headerLabel"
      disabled={selectionType === 'year'}
      on:click|preventDefault={() => (selectionType = 'year')}>
      {new Date(year, month).toLocaleDateString(
        undefined,
        selectionType === 'day'
          ? {month: 'long', year: 'numeric'}
          : {year: 'numeric'}
      )}
    </button>

    {#if selectionType !== 'year'}
      <button
        class="headerArrow"
        disabled={maxDate &&
          maxDate <=
            (selectionType === 'month' ? currentYearEnd : currentMonthEnd)}
        on:click|preventDefault={selectionType === 'month'
          ? showNextYear
          : showNextMonth}>
        <InlineSVG src={nextIcon} />
      </button>
    {/if}
  </header>

  <div class="calendarContent">
    {#if selectionType === 'year'}
      <YearSelection {selectedDate} {minDate} {maxDate} on:select={showYear} />
    {:else if selectionType === 'month'}
      <MonthSelection
        {selectedDate}
        {minDate}
        {maxDate}
        {year}
        on:select={showMonth} />
    {:else}
      <DaySelection
        {type}
        {selectedDate}
        {minDate}
        {maxDate}
        {year}
        {month}
        {weekdays}
        {weekdayOffset}
        {previousMonth}
        {currentMonth}
        {nextMonth}
        on:change />
    {/if}
  </div>
</div>
